/**
 * Kodeon API Client
 *
 * Single source of truth for all platform communication.
 * Handles HTTP requests and SSE streaming with gateway-native authentication.
 *
 * Architecture: Layer 2 (TypeScript API Client)
 * - No Rust proxy for HTTP/SSE
 * - Types defined once in TypeScript
 * - Consumed by both UI and agents (transparency principle)
 */

/**
 * Fallback gateway URL from Vite build environment.
 * Used when no runtime gateway URL is provided (e.g., during initial configuration).
 * The runtime URL provided by the Rust layer takes precedence.
 */
const VITE_GATEWAY_URL: string | undefined = import.meta.env.VITE_GATEWAY_URL

/**
 * Standard backend error response format.
 */
interface ApiError {
  error: {
    message: string
    code?: string
    details?: unknown
    required_scopes?: string[]
  }
}

/**
 * Custom error class that preserves HTTP status code and structured error fields.
 *
 * For INSUFFICIENT_SCOPE (403) responses from RequireScope middleware, `code`
 * will be "INSUFFICIENT_SCOPE" and `requiredScopes` will contain the scope(s)
 * the caller would have needed (Task 3552c).
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly statusText: string,
    public readonly code?: string,
    public readonly requiredScopes?: string[],
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

/**
 * Kodeon API Client class.
 *
 * Provides typed HTTP methods and SSE streaming with automatic header injection.
 * Must be configured with an API key before use via configure(apiKey, gatewayUrl?).
 *
 * Supports runtime reconfiguration for environment switching. When reconfigure()
 * is called, all active SSE streams are aborted before swapping configuration.
 */
/**
 * Three base64url segments — the shape of a JWT, hence of a Google ID token.
 *
 * Segments carry a minimum length because the alphabet alone is too permissive:
 * "a.b.c" matches a bare-alphabet check and would be sent as a bearer token
 * (PR #109 N1). A real JWT header is at least `eyJhbGciOi...` — base64url of
 * `{"alg":...`. 8 characters is comfortably below any genuine segment and well
 * above the junk this is meant to reject.
 */
const JWT_SHAPE = /^[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}$/

/**
 * Supplies a fresh credential after a 401, or null when refresh is impossible.
 * Injected rather than imported so the client stays free of a dependency on the
 * auth feature (which imports the client).
 */
export type CredentialRefresher = () => Promise<string | null>

/** How the configured credential is presented to the gateway. */
export type CredentialKind = 'api_key' | 'bearer'

/**
 * Classify a credential, or return null if it is neither recognised form.
 *
 * `ak_` is unambiguous. Anything else must look like a JWT — three
 * dot-separated non-empty segments — which is what a Google ID token is. This
 * is a shape check, not validation: the gateway verifies the signature, `iss`,
 * `hd`, `email_verified` and `aud`. The point here is only to fail fast on an
 * obviously wrong value (empty string, a bare email, a stray URL) rather than
 * sending it and getting an opaque 401.
 */
export function classifyCredential(credential: string): CredentialKind | null {
  if (!credential) return null
  if (credential.startsWith('ak_')) {
    return credential.length >= 10 ? 'api_key' : null
  }
  // A JWT is three base64url segments of non-trivial length. Both parts of that
  // matter: "https://accounts.google.com" is three dot-separated parts (caught
  // by the alphabet), and "a.b.c" passes an alphabet-only check (caught by the
  // length floor).
  if (JWT_SHAPE.test(credential)) {
    return 'bearer'
  }
  return null
}

export class KodeonApiClient {
  private credential: string | null = null
  /**
   * Which auth header the credential is sent as.
   *
   * - `api_key` — `X-Kodeon-Api-Key: ak_...` (Stream desktop, and any consumer
   *   holding a provisioned key).
   * - `bearer` — `Authorization: Bearer <google-id-token>` (the browser portal,
   *   Task 4092). The gateway's staff auth middleware verifies the token and
   *   swaps it for an internal JWT before proxying downstream.
   *
   * Both are first-class: the desktop app is not going away when the portal
   * ships, so this is a lasting distinction rather than a migration shim.
   */
  private credentialKind: CredentialKind = 'api_key'
  private gatewayUrl: string | null = null

  /**
   * Optional hook used to recover from a 401 exactly once per request
   * (Task 4092 step 4). Unset on desktop, where the Rust layer mints a fresh
   * token per call and a 401 is therefore genuine.
   */
  private credentialRefresher: CredentialRefresher | null = null

  /**
   * Tracks all active AbortControllers for SSE streams (subscribe + streamPost).
   * On reconfigure(), all tracked controllers are aborted to cleanly tear down
   * active connections before switching environments.
   */
  private activeAbortControllers = new Set<AbortController>()

  /**
   * Configure the client with a credential and optional gateway URL.
   * Must be called at app startup before making any API requests.
   *
   * The credential is either a provisioned API key (`ak_...`) or a Google ID
   * token. The kind is inferred from the value rather than passed explicitly,
   * so the ~40 existing call sites keep working unchanged: an `ak_` prefix is
   * unambiguous, and Google ID tokens are JWTs, which cannot collide with it.
   *
   * @param credential - `ak_...` API key, or a Google ID token (browser portal)
   * @param gatewayUrl - Optional gateway URL (falls back to VITE_GATEWAY_URL)
   */
  configure(credential: string, gatewayUrl?: string): void {
    const kind = classifyCredential(credential)
    if (!kind) {
      const preview = credential ? credential.slice(0, 20) : '(empty)'
      throw new Error(
        `Invalid credential. Expected an 'ak_...' API key (min 10 chars) or a Google ID token (JWT), ` +
        `but got: '${preview}${credential && credential.length > 20 ? '...' : ''}'`,
      )
    }

    const resolvedUrl = gatewayUrl || VITE_GATEWAY_URL
    if (!resolvedUrl) {
      throw new Error(
        'No gateway URL provided and VITE_GATEWAY_URL is not set. ' +
        'Provide a gatewayUrl argument or set VITE_GATEWAY_URL.'
      )
    }

    this.credential = credential
    this.credentialKind = kind
    this.gatewayUrl = resolvedUrl
  }

  /**
   * Reconfigure the client for a new environment.
   * Aborts all active SSE streams, then applies new configuration.
   *
   * @param apiKey - The new API key
   * @param gatewayUrl - The new gateway URL
   */
  reconfigure(apiKey: string, gatewayUrl: string): void {
    // Validate BEFORE mutating (PR #109 B4). configure() throws on a bad
    // credential, and it used to throw after abortAllStreams() had already run
    // — leaving the client unconfigured with every stream torn down and
    // unrecoverable without a fresh configure(). A malformed key from Secret
    // Manager would kill the operator's queue and the client with it.
    if (!classifyCredential(apiKey)) {
      throw new Error('Invalid credential passed to reconfigure')
    }

    this.abortAllStreams()
    this.credential = null  // Reset so configure() can set it
    this.credentialKind = 'api_key'
    this.gatewayUrl = null
    this.configure(apiKey, gatewayUrl)
  }

  /**
   * Install the hook that recovers from a 401 by obtaining a new credential.
   * Passing null disables retry-on-401.
   */
  setCredentialRefresher(refresher: CredentialRefresher | null): void {
    this.credentialRefresher = refresher
  }

  /**
   * Drop the configured credential, leaving the gateway URL in place.
   *
   * configure()/updateCredential() both reject empty input, so this is the only
   * way to return the client to an unauthenticated state — needed on sign-out,
   * where leaving the credential installed means requests keep going out under
   * a "signed out" UI (PR #109 B1).
   */
  clearCredential(): void {
    this.credential = null
    this.credentialKind = 'api_key'
  }

  /**
   * Swap the credential in place, keeping the gateway and every active stream.
   *
   * This is NOT reconfigure(): a token refresh (Task 4092 step 4) must not tear
   * down the operator's work. reconfigure() aborts all SSE streams because an
   * environment switch genuinely invalidates them — a new token against the
   * same gateway does not. Long-lived streams already authenticated at
   * connection setup and keep running; new requests pick up the new credential.
   *
   * @param credential - The replacement credential, same forms as configure()
   */
  updateCredential(credential: string): void {
    const kind = classifyCredential(credential)
    if (!kind) {
      throw new Error('Invalid credential passed to updateCredential')
    }
    this.credential = credential
    this.credentialKind = kind
  }

  /**
   * Abort all active SSE streams.
   * Called internally by reconfigure() and can be called externally if needed.
   */
  abortAllStreams(): void {
    for (const controller of this.activeAbortControllers) {
      controller.abort()
    }
    this.activeAbortControllers.clear()
  }

  /**
   * Get the configured credential.
   * Used by features that need manual header construction (e.g. XMLHttpRequest
   * for upload progress, which cannot reuse getHeaders()).
   *
   * Callers that build headers by hand must use {@link getAuthHeaders} instead
   * of assuming the API-key header — under bearer auth, `X-Kodeon-Api-Key`
   * carries a Google ID token the gateway will not read.
   *
   * @returns The configured credential, or null if not configured
   */
  getApiKey(): string | null {
    return this.credential
  }

  /**
   * The auth header(s) for the current credential, for callers that construct
   * requests outside this client. Returns an empty object when unconfigured;
   * callers already guard on {@link getApiKey}.
   */
  getAuthHeaders(): Record<string, string> {
    if (!this.credential) return {}
    return this.credentialKind === 'bearer'
      ? { Authorization: `Bearer ${this.credential}` }
      : { 'X-Kodeon-Api-Key': this.credential }
  }

  /**
   * Get the configured gateway URL.
   * Used by features that need to build URLs for browser-native elements
   * (e.g. <img src>, <iframe src>).
   *
   * @returns The configured gateway URL, or null if not configured
   */
  getGatewayUrl(): string | null {
    return this.gatewayUrl
  }

  /**
   * Internal helper: Build full URL from path.
   */
  private buildUrl(path: string): string {
    if (!this.gatewayUrl) {
      throw new Error('API client not configured. Call configure(apiKey, gatewayUrl?) before making requests.')
    }
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return `${this.gatewayUrl}${normalizedPath}`
  }

  /**
   * Fetch with a single refresh-and-replay on 401 (Task 4092 step 4).
   *
   * Every authenticated fetch goes through this so the token-expiry path is
   * uniform. It is not enough for request() alone to retry: an operator idling
   * on the queue page issues no ordinary requests, so the SSE stream is exactly
   * the surface that hits expiry first — and without a retry here it
   * reconnect-loops forever against a dead token (PR #109 B2).
   *
   * `buildInit` is re-invoked for the replay rather than reusing the original
   * RequestInit, so the second attempt carries the NEW credential. Reusing it
   * would resend the expired token and make the retry pointless.
   *
   * A refresher that throws is treated as a refresh failure, so the caller sees
   * the original 401 rather than an unrelated error (PR #109 B3).
   */
  private async fetchWithRefresh(
    url: string,
    buildInit: () => RequestInit,
  ): Promise<Response> {
    const response = await fetch(url, buildInit())
    if (response.status !== 401 || !this.credentialRefresher) {
      return response
    }

    let refreshed: string | null = null
    try {
      refreshed = await this.credentialRefresher()
    } catch {
      refreshed = null
    }
    if (!refreshed) {
      return response
    }
    return fetch(url, buildInit())
  }

  /**
   * Internal helper: Get headers with API key injection.
   */
  private getHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
    if (!this.credential) {
      throw new Error('API client not configured. Call configure(credential) before making requests.')
    }

    return {
      ...this.getAuthHeaders(),
      ...additionalHeaders,
    }
  }

  /**
   * Internal helper: Create a tracked AbortController that is linked to an
   * optional external signal. The internal controller is added to the tracking
   * set and removed when the stream completes or is aborted.
   */
  private createTrackedAbortController(externalSignal?: AbortSignal): AbortController {
    const controller = new AbortController()
    this.activeAbortControllers.add(controller)

    // If an external signal is provided, link it: when external aborts, abort internal too
    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort()
      } else {
        const onExternalAbort = () => controller.abort()
        externalSignal.addEventListener('abort', onExternalAbort, { once: true })
        // Clean up listener when internal controller aborts
        controller.signal.addEventListener('abort', () => {
          externalSignal.removeEventListener('abort', onExternalAbort)
        }, { once: true })
      }
    }

    return controller
  }

  /**
   * Internal helper: Remove a tracked AbortController from the tracking set.
   */
  private untrackAbortController(controller: AbortController): void {
    this.activeAbortControllers.delete(controller)
  }

  /**
   * Internal helper: Generic request handler.
   *
   * @param method - HTTP method
   * @param path - API path (e.g., '/v1/customers')
   * @param options - Request options (body, query params, etc.)
   * @returns Parsed JSON response
   * @throws Error with backend error message if request fails
   */
  private async request<T>(
    method: string,
    path: string,
    options: {
      body?: unknown
      params?: Record<string, string | number | boolean | Array<string | number | boolean>>
      headers?: Record<string, string>
      signal?: AbortSignal
    } = {}
  ): Promise<T> {
    const url = new URL(this.buildUrl(path))

    // Add query parameters if provided.
    // Array values are appended as repeated parameters (e.g., expand[]=foo&expand[]=bar)
    // — required by backends that expect repeated query params for list-typed inputs.
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => url.searchParams.append(key, String(item)))
        } else {
          url.searchParams.append(key, String(value))
        }
      })
    }

    const buildFetchOptions = (): RequestInit => {
      const headers = this.getHeaders(options.headers)
      const fetchOptions: RequestInit = { method, headers }

      // Forward an optional caller AbortSignal so in-flight requests can be
      // cancelled (e.g. a component unmounts while a translation is pending).
      if (options.signal) {
        fetchOptions.signal = options.signal
      }

      // Add JSON body if provided
      if (options.body !== undefined) {
        fetchOptions.body = JSON.stringify(options.body)
        headers['Content-Type'] = 'application/json'
      }
      return fetchOptions
    }

    // Retry-on-401 once. Safe for writes: buildFetchOptions re-serialises the
    // body from options.body, so nothing consumes a one-shot stream.
    const response = await this.fetchWithRefresh(url.toString(), buildFetchOptions)

    // Handle non-OK responses
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`
      let errorCode: string | undefined
      let requiredScopes: string[] | undefined

      // Try to parse backend error format
      try {
        const errorData: ApiError = await response.json()
        if (errorData.error?.message) {
          errorMessage = errorData.error.message
        }
        errorCode = errorData.error?.code
        requiredScopes = errorData.error?.required_scopes
      } catch {
        // If JSON parsing fails, use status text
      }

      throw new ApiClientError(errorMessage, response.status, response.statusText, errorCode, requiredScopes)
    }

    // Handle empty responses (204 No Content, or empty 200 OK)
    const contentLength = response.headers.get('content-length')
    if (contentLength === '0' || response.status === 204) {
      return undefined as T
    }

    // Parse and return JSON response
    try {
      return await response.json()
    } catch (err) {
      throw new Error(`Failed to parse response JSON: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  /**
   * GET request.
   *
   * @param path - API path (e.g., '/v1/customers')
   * @param params - Optional query parameters. Array values are appended as repeated
   *                parameters (e.g., expand[]=foo&expand[]=bar).
   * @returns Typed response
   */
  async get<T>(
    path: string,
    params?: Record<string, string | number | boolean | Array<string | number | boolean>>
  ): Promise<T> {
    return this.request<T>('GET', path, { params })
  }

  /**
   * Fetch raw text from an API path with optional extra headers (e.g. Range).
   * Returns null on any error so callers can degrade gracefully.
   *
   * Used by features that need direct byte-range access (e.g. preview of large
   * text/code/JSON files via /v1/files/{id}/content).
   */
  async fetchText(
    path: string,
    extraHeaders: Record<string, string> = {}
  ): Promise<string | null> {
    try {
      const url = this.buildUrl(path)
      const response = await this.fetchWithRefresh(url, () => ({
        method: 'GET',
        headers: this.getHeaders(extraHeaders),
      }))
      if (!response.ok) return null
      return await response.text()
    } catch {
      return null
    }
  }

  /**
   * Return the absolute URL for a given API path.
   * Use for browser-native elements (<img src>, <iframe src>) where custom
   * request headers cannot be injected by JavaScript.
   *
   * Note: browser-native requests will be unauthenticated unless the backend
   * supports cookie-based auth or signed/time-limited URLs. This is a known
   * backend gap documented in task 2572 §15.
   */
  buildAbsoluteUrl(path: string): string {
    return this.buildUrl(path)
  }

  /**
   * POST request.
   *
   * @param path - API path (e.g., '/v1/customers')
   * @param body - Request body (will be JSON-serialized)
   * @param signal - Optional AbortSignal to cancel the request
   * @returns Typed response
   */
  async post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>('POST', path, { body, signal })
  }

  /**
   * PUT request.
   *
   * @param path - API path (e.g., '/v1/customers/123')
   * @param body - Request body (will be JSON-serialized)
   * @returns Typed response
   */
  async put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PUT', path, { body })
  }

  /**
   * PATCH request.
   *
   * @param path - API path (e.g., '/v1/customers/123')
   * @param body - Request body (will be JSON-serialized)
   * @returns Typed response
   */
  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, { body })
  }

  /**
   * DELETE request.
   *
   * @param path - API path (e.g., '/v1/customers/123')
   * @returns Response (often empty)
   */
  async delete<T = void>(path: string): Promise<T> {
    return this.request<T>('DELETE', path)
  }

  /**
   * POST request returning a Blob (for binary downloads like zip files).
   * Used for endpoints that return binary content instead of JSON.
   *
   * @param path - API path (e.g., '/v1/files/bulk-download')
   * @param body - Request body (will be JSON-serialized)
   * @returns Blob response
   */
  async postBlob(path: string, body: unknown): Promise<Blob> {
    const url = this.buildUrl(path)

    const response = await this.fetchWithRefresh(url, () => ({
      method: 'POST',
      headers: this.getHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    }))

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`
      let errorCode: string | undefined
      let requiredScopes: string[] | undefined

      // Try to parse backend error format (if response is JSON)
      try {
        const errorData: ApiError = await response.json()
        if (errorData.error?.message) {
          errorMessage = errorData.error.message
        }
        errorCode = errorData.error?.code
        requiredScopes = errorData.error?.required_scopes
      } catch {
        // If JSON parsing fails, use status text
      }

      throw new ApiClientError(errorMessage, response.status, response.statusText, errorCode, requiredScopes)
    }

    return response.blob()
  }

  /**
   * Server-Sent Events (SSE) subscription for GET-based streaming.
   *
   * Native browser EventSource does not support custom headers,
   * so we use fetch + ReadableStream with manual SSE parsing.
   *
   * SSE format:
   * ```
   * event: message
   * data: {"key": "value"}
   *
   * event: error
   * data: {"error": "..."}
   * ```
   *
   * Error handling:
   * - Connection errors: throws (or calls onError if provided) and terminates
   * - Individual message parse errors: calls onError but continues stream
   * - AbortSignal: terminates stream immediately and releases resources
   *
   * Stream tracking:
   * - The internal AbortController is tracked in activeAbortControllers
   * - On reconfigure(), all tracked streams are aborted automatically
   * - The caller's optional signal is linked to the internal controller
   *
   * @param path - API path (e.g., '/v1/events')
   * @param onMessage - Callback for each parsed event
   * @param onError - Optional error handler (called for parse errors and stream errors)
   * @param signal - Optional AbortSignal to cancel the stream
   */
  async subscribe<T>(
    path: string,
    onMessage: (data: T) => void,
    onError?: (error: Error) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const url = this.buildUrl(path)

    // Create a tracked abort controller linked to the caller's signal
    const trackedController = this.createTrackedAbortController(signal)

    try {
      const response = await this.fetchWithRefresh(url, () => ({
        method: 'GET',
        headers: this.getHeaders({ 'Accept': 'text/event-stream' }),
        signal: trackedController.signal,
      }))

      if (!response.ok) {
        // Try to parse error response for better debugging
        let errorMessage = `SSE connection failed: HTTP ${response.status}`
        try {
          const errorData: ApiError = await response.json()
          if (errorData.error?.message) {
            errorMessage = `SSE connection failed: ${errorData.error.message}`
          }
        } catch {
          // If JSON parsing fails, use status code
        }
        throw new Error(errorMessage)
      }

      if (!response.body) {
        throw new Error('SSE response has no body')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      try {
        // Read stream chunks
        while (true) {
          // Check if aborted before attempting read
          if (trackedController.signal.aborted) {
            break
          }

          const { done, value } = await reader.read()

          if (done) {
            break
          }

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true })

          // Process complete events (separated by double newline)
          const events = buffer.split('\n\n')
          // Keep incomplete event in buffer
          buffer = events.pop() || ''

          for (const event of events) {
            if (!event.trim()) continue

            const parsed = this.parseSSEEvent(event)
            if (parsed) {
              // Handle error events from server
              if (parsed.event === 'error') {
                if (onError) {
                  try {
                    const errorData = JSON.parse(parsed.data)
                    const message = errorData.message || errorData.error || 'SSE stream error'
                    onError(new Error(message))
                  } catch {
                    // If error data is not JSON, use raw data
                    onError(new Error(`SSE stream error: ${parsed.data}`))
                  }
                }
                continue
              }

              // Process data events: "event" and "message" types (ignore control events like "connected", "heartbeat", "reconnect")
              if (parsed.event === 'event' || parsed.event === 'message') {
                try {
                  // Parse JSON data field
                  const data = JSON.parse(parsed.data) as T
                  onMessage(data)
                } catch (err) {
                  // JSON parse error - call error handler if provided
                  if (onError) {
                    onError(new Error(`Failed to parse SSE data: ${err}`))
                  }
                }
              }
              // Silently ignore other event types (connected, heartbeat, reconnect, etc.)
            }
          }
        }
      } finally {
        // Always release the reader lock, even if aborted or errored
        reader.releaseLock()
      }
    } catch (err) {
      // Suppress AbortError - this is expected when signal.abort() is called
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }

      if (onError) {
        onError(err instanceof Error ? err : new Error(String(err)))
      } else {
        // Re-throw if no error handler provided
        throw err
      }
    } finally {
      // Always untrack when stream ends (naturally, by error, or by abort)
      this.untrackAbortController(trackedController)
    }
  }

  /**
   * Server-Sent Events (SSE) subscription for POST-based streaming.
   *
   * Similar to subscribe() but sends a POST request with a JSON body.
   * Used for agent session streaming and other stateful SSE endpoints.
   *
   * Error handling:
   * - Connection errors: throws (or calls onError if provided) and terminates
   * - Individual message parse errors: calls onError but continues stream
   * - AbortSignal: terminates stream immediately and releases resources
   *
   * Stream tracking:
   * - The internal AbortController is tracked in activeAbortControllers
   * - On reconfigure(), all tracked streams are aborted automatically
   * - The caller's optional signal is linked to the internal controller
   *
   * @param path - API path (e.g., '/v1/agents/123/stream')
   * @param body - Request body (will be JSON-serialized)
   * @param onMessage - Callback for each parsed event
   * @param onError - Optional error handler (called for parse errors and stream errors)
   * @param signal - Optional AbortSignal to cancel the stream
   */
  async streamPost<T>(
    path: string,
    body: unknown,
    onMessage: (data: T) => void,
    onError?: (error: Error) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const url = this.buildUrl(path)
    // Create a tracked abort controller linked to the caller's signal
    const trackedController = this.createTrackedAbortController(signal)

    try {
      const response = await this.fetchWithRefresh(url, () => ({
        method: 'POST',
        headers: this.getHeaders({
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        }),
        body: JSON.stringify(body),
        signal: trackedController.signal,
      }))

      if (!response.ok) {
        // Try to parse error response for better debugging
        let errorMessage = `SSE POST connection failed: HTTP ${response.status}`
        try {
          const errorData: ApiError = await response.json()
          if (errorData.error?.message) {
            errorMessage = `SSE POST connection failed: ${errorData.error.message}`
          }
        } catch {
          // If JSON parsing fails, use status code
        }
        throw new Error(errorMessage)
      }

      if (!response.body) {
        throw new Error('SSE POST response has no body')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      try {
        // Read stream chunks
        while (true) {
          // Check if aborted before attempting read
          if (trackedController.signal.aborted) {
            break
          }

          const { done, value } = await reader.read()

          if (done) {
            break
          }

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true })

          // Process complete events (separated by double newline)
          const events = buffer.split('\n\n')
          // Keep incomplete event in buffer
          buffer = events.pop() || ''

          for (const event of events) {
            if (!event.trim()) continue


            const parsed = this.parseSSEEvent(event)
            if (parsed) {
              // Handle error events from server
              if (parsed.event === 'error') {
                if (onError) {
                  try {
                    const errorData = JSON.parse(parsed.data)
                    const message = errorData.message || errorData.error || 'SSE stream error'
                    onError(new Error(message))
                  } catch {
                    // If error data is not JSON, use raw data
                    onError(new Error(`SSE stream error: ${parsed.data}`))
                  }
                }
                continue
              }

              // Process data events: "event" and "message" types (ignore control events like "connected", "heartbeat", "reconnect")
              if (parsed.event === 'event' || parsed.event === 'message') {
                try {
                  // Parse JSON data field
                  const data = JSON.parse(parsed.data) as T
                  onMessage(data)
                } catch (err) {
                  // JSON parse error - call error handler if provided
                  if (onError) {
                    onError(new Error(`Failed to parse SSE data: ${err}`))
                  }
                }
              }
              // Silently ignore other event types (connected, heartbeat, reconnect, etc.)
            }
          }
        }
      } finally {
        // Always release the reader lock, even if aborted or errored
        reader.releaseLock()
      }
    } catch (err) {
      // Suppress AbortError - this is expected when signal.abort() is called
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }

      if (onError) {
        onError(err instanceof Error ? err : new Error(String(err)))
      } else {
        // Re-throw if no error handler provided
        throw err
      }
    } finally {
      // Always untrack when stream ends (naturally, by error, or by abort)
      this.untrackAbortController(trackedController)
    }
  }

  /**
   * Internal helper: Parse a single SSE event.
   *
   * SSE format:
   * ```
   * event: message
   * data: {"key": "value"}
   * ```
   *
   * Multi-line data is concatenated with newlines per SSE spec:
   * ```
   * event: message
   * data: line 1
   * data: line 2
   * ```
   * becomes: "line 1\nline 2"
   *
   * @param eventText - Raw event text
   * @returns Parsed event with type, data, and optional id, or null if invalid
   */
  private parseSSEEvent(eventText: string): { event: string; data: string; id?: string } | null {
    const lines = eventText.split('\n')
    let eventType = 'message' // Default event type
    const dataLines: string[] = []
    let eventId: string | undefined

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        // Multiple data lines are concatenated with newlines per SSE spec
        dataLines.push(line.slice(5).trim())
      } else if (line.startsWith('id:')) {
        eventId = line.slice(3).trim()
      }
      // Ignore comment lines (starting with ':') and other fields
    }

    // Only return if we have data
    if (dataLines.length > 0) {
      const result: { event: string; data: string; id?: string } = {
        event: eventType,
        data: dataLines.join('\n'),
      }
      if (eventId) {
        result.id = eventId
      }
      return result
    }

    return null
  }
}
